import type { SensorType } from '@si/types';
import { patch } from '@si/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { queryKeys } from './queryKeys';

interface InstallationTaskMetaUpdates {
  label: string;
  comment: string;
  sensorType?: SensorType;
  woodScrewLengthMm?: number | null;
  woodInsulatedElectrode?: boolean | null;
}

interface Params {
  projectId: string;
  installationId: string;
  installationTaskId: number;
  updates: InstallationTaskMetaUpdates;
}

export function useUpdateInstallationTaskMeta() {
  const queryClient = useQueryClient();

  const { mutateAsync, error, isPending, isSuccess, reset } = useMutation({
    mutationFn: (params: Params) =>
      patch({
        url: `/projects/${params.projectId}/installation-tasks/${params.installationTaskId}`,
        body: params.updates,
      }),
    onSuccess: (_, { projectId, installationId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.getInstallationTasks(projectId, installationId),
      });
    },
  });

  const updateInstallationTaskMetaAsync = useCallback(
    (params: Params) => mutateAsync(params),
    [mutateAsync]
  );

  return {
    updateInstallationTaskMetaAsync,
    error,
    isPending,
    isSuccess,
    reset,
  };
}
